import { fileExists } from '../services/FileSystemService';

// ============================================================================
// TypeScript Declarations for WebKit File System Entry API
// ============================================================================

declare global {
  interface DataTransferItem {
    webkitGetAsEntry?(): FileSystemEntry | null;
  }

  interface FileSystemEntry {
    readonly isFile: boolean;
    readonly isDirectory: boolean;
    readonly name: string;
    readonly fullPath: string;
    readonly filesystem: FileSystem;
  }

  interface FileSystemFileEntry extends FileSystemEntry {
    file(successCallback: (file: File) => void, errorCallback?: (error: Error) => void): void;
  }

  interface FileSystemDirectoryEntry extends FileSystemEntry {
    createReader(): FileSystemDirectoryReader;
  }

  interface FileSystemDirectoryReader {
    readEntries(
      successCallback: (entries: FileSystemEntry[]) => void,
      errorCallback?: (error: Error) => void
    ): void;
  }

  interface FileSystem {
    readonly name: string;
    readonly root: FileSystemDirectoryEntry;
  }
}

// ============================================================================
// Constants and Utilities
// ============================================================================

export const SUPPORTED_IMPORT_EXTENSIONS = new Set([
  // Images
  '.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg',
  // Video
  '.mp4', '.webm', '.mov', '.avi', '.mkv',
  // Audio
  '.mp3', '.ogg', '.wav', '.m4a', '.flac', '.aac',
  // Scripts
  '.js', '.ts', '.jsx', '.tsx',
  // Fonts
  '.ttf', '.otf', '.woff', '.woff2',
  // Data
  '.json',
  // Spine
  '.skel', '.atlas',
  // Editor formats
  '.scene', '.prefab',
]);

export function isSupportedFileType(fileName: string): boolean {
  const ext = getFileExtension(fileName).toLowerCase();
  return SUPPORTED_IMPORT_EXTENSIONS.has(ext);
}

export function getFileExtension(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex !== -1 ? fileName.slice(lastDotIndex) : '';
}

export function getBaseName(fileName: string): string {
  const lastDotIndex = fileName.lastIndexOf('.');
  return lastDotIndex !== -1 ? fileName.slice(0, lastDotIndex) : fileName;
}

export async function getUniqueFileName(
  folderPath: string,
  fileName: string
): Promise<string> {
  const baseName = getBaseName(fileName);
  const extension = getFileExtension(fileName);

  const normalizedFolder = folderPath.startsWith('/')
    ? folderPath.slice(1)
    : folderPath;

  const originalPath = normalizedFolder
    ? `${normalizedFolder}/${fileName}`
    : fileName;

  if (!(await fileExists(originalPath))) {
    return fileName;
  }

  let counter = 1;
  const maxAttempts = 100;

  while (counter <= maxAttempts) {
    const newName = `${baseName}_${counter}${extension}`;
    const newPath = normalizedFolder
      ? `${normalizedFolder}/${newName}`
      : newName;

    if (!(await fileExists(newPath))) {
      return newName;
    }
    counter++;
  }

  return `${baseName}_${Date.now()}${extension}`;
}

export function isImageFile(fileName: string): boolean {
  const ext = getFileExtension(fileName).toLowerCase();
  return ['.png', '.jpg', '.jpeg', '.gif', '.webp', '.bmp', '.svg'].includes(ext);
}

export function validateFileForImport(file: File): string | null {
  const maxSize = 100 * 1024 * 1024; // 100MB
  if (file.size > maxSize) {
    return `File "${file.name}" is too large (max 100MB)`;
  }

  if (!isSupportedFileType(file.name)) {
    return `File type not supported: "${file.name}"`;
  }

  if (file.size === 0) {
    return `File "${file.name}" is empty`;
  }

  return null;
}

export interface FileImportResult {
  originalName: string;
  finalName: string;
  success: boolean;
  error?: string;
  path?: string;
}

/**
 * Represents a file entry from a dropped folder for import
 */
export interface DroppedFileEntry {
  file: File;
  relativePath: string; // Path relative to the dropped folder root
}

/**
 * Recursively reads all files from a FileSystemEntry (from webkitGetAsEntry)
 * @param entry - The FileSystemEntry to read
 * @param basePath - The base path for relative path calculation
 * @returns Array of DroppedFileEntry objects
 */
export async function readEntriesRecursively(
  entry: FileSystemEntry,
  basePath: string = ''
): Promise<DroppedFileEntry[]> {
  const results: DroppedFileEntry[] = [];

  if (entry.isFile) {
    const fileEntry = entry as FileSystemFileEntry;
    const file = await getFileFromEntry(fileEntry);
    if (file) {
      results.push({
        file,
        relativePath: basePath ? `${basePath}/${file.name}` : file.name,
      });
    }
  } else if (entry.isDirectory) {
    const dirEntry = entry as FileSystemDirectoryEntry;
    const dirReader = dirEntry.createReader();
    const entries = await readAllDirectoryEntries(dirReader);

    const newBasePath = basePath ? `${basePath}/${entry.name}` : entry.name;

    for (const childEntry of entries) {
      const childResults = await readEntriesRecursively(childEntry, newBasePath);
      results.push(...childResults);
    }
  }

  return results;
}

/**
 * Converts a FileSystemFileEntry to a File object
 */
function getFileFromEntry(fileEntry: FileSystemFileEntry): Promise<File | null> {
  return new Promise((resolve) => {
    fileEntry.file(
      (file) => resolve(file),
      () => resolve(null)
    );
  });
}

/**
 * Reads all entries from a directory reader (handles batching)
 */
function readAllDirectoryEntries(
  dirReader: FileSystemDirectoryReader
): Promise<FileSystemEntry[]> {
  return new Promise((resolve, reject) => {
    const allEntries: FileSystemEntry[] = [];

    const readBatch = () => {
      dirReader.readEntries(
        (entries) => {
          if (entries.length === 0) {
            resolve(allEntries);
          } else {
            allEntries.push(...entries);
            readBatch(); // Continue reading (directory reader returns batches)
          }
        },
        (error) => reject(error)
      );
    };

    readBatch();
  });
}

/**
 * Process DataTransferItemList and extract all files/folders as DroppedFileEntry[]
 * @param items - DataTransferItemList from drop event
 * @returns Array of DroppedFileEntry objects with files and their relative paths
 */
export async function processDroppedItems(
  items: DataTransferItemList
): Promise<DroppedFileEntry[]> {
  const results: DroppedFileEntry[] = [];
  const itemsArray = Array.from(items);

  for (const item of itemsArray) {
    if (item.kind !== 'file') continue;

    const entry = item.webkitGetAsEntry?.();

    if (entry) {
      // Use webkitGetAsEntry for better folder support
      const entries = await readEntriesRecursively(entry);
      results.push(...entries);
    } else {
      // Fallback to regular file handling
      const file = item.getAsFile();
      if (file) {
        results.push({
          file,
          relativePath: file.name,
        });
      }
    }
  }

  return results;
}

/**
 * Extract unique folder paths from a list of dropped file entries
 * @param entries - Array of DroppedFileEntry objects
 * @returns Set of unique folder paths that need to be created
 */
export function extractFolderPaths(entries: DroppedFileEntry[]): Set<string> {
  const folders = new Set<string>();

  for (const entry of entries) {
    const parts = entry.relativePath.split('/');
    // Remove the filename, keep only directory parts
    parts.pop();

    // Build up all parent paths
    let currentPath = '';
    for (const part of parts) {
      currentPath = currentPath ? `${currentPath}/${part}` : part;
      folders.add(currentPath);
    }
  }

  return folders;
}
